/**
 * PromptDetailDialog — 탭3 (파이프라인) 의 한 prompt 행을 풀뷰로 여는 모달.
 *
 * 본 스펙: AIFYAGENT/MONITORING_SPEC.md §6.b
 *   - fix_attempts.json 전체 (attempts 배열, fix_patterns_applied, evidence)
 *   - validation_report.json 전체 (4종 체크 결과)
 *   - HAI 파일 GCS 링크 (prefix 브라우저)
 *   - script.py GCS 링크 (파일 상세)
 *
 * Phase 2 가 켜져 있을 때 (REACT_APP_FEATURE_MONITORING_V2=true) PipelineTable 행에서 호출.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  fetchPromptDetail,
  gcsConsoleUrl,
  type FixAttemptEntry,
  type PromptDetailResponse,
  type ValidationReportRaw,
} from "../api/monitoring";

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  promptId: string;
  bsda: string;
}

export function PromptDetailDialog({ open, onClose, tenantId, promptId, bsda }: Props) {
  const [detail, setDetail] = useState<PromptDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId || !promptId || !bsda) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPromptDetail(tenantId, promptId, bsda);
      setDetail(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId, promptId, bsda]);

  useEffect(() => {
    if (open) {
      setDetail(null);
      void load();
    }
  }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            세션 상세
          </Typography>
          <Chip size="small" variant="outlined" label={`테넌트: ${tenantId}`} />
          <Chip size="small" variant="outlined" label={`prompt: ${promptId}`} />
          <Chip size="small" variant="outlined" label={`BSDA: ${bsda}`} />
        </Stack>
        <IconButton size="small" onClick={() => void load()} disabled={loading} aria-label="reload">
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {loading && !detail && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {detail && (
          <Stack spacing={2}>
            <OverallStatus detail={detail} />
            <GcsLinksBlock detail={detail} />
            <Divider />
            <FixAttemptsBlock detail={detail} />
            <Divider />
            <ValidationReportBlock report={detail.validation_report} />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OverallStatus({ detail }: { detail: PromptDetailResponse }) {
  const fa = detail.fix_attempts;
  const vr = detail.validation_report;
  const overall = fa?.overall_status ?? "(no fix_loop)";
  const valStatus = vr?.overall_status ?? "(no validation)";
  const overallColor: "success" | "error" | "warning" | "info" | "default" =
    overall === "PASS"
      ? "success"
      : overall === "ESCALATED"
        ? "error"
        : overall === "FAIL"
          ? "error"
          : overall === "IN_PROGRESS"
            ? "info"
            : "default";
  const valColor: "success" | "error" | "default" =
    valStatus === "PASS" ? "success" : valStatus === "FAIL" ? "error" : "default";
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        fix_loop:
      </Typography>
      <Chip size="small" color={overallColor} label={overall} />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
        validation:
      </Typography>
      <Chip size="small" color={valColor} label={valStatus} />
      {fa?.attempt_count != null && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          attempts {fa.attempt_count}/{fa.max_retries ?? 3}
        </Typography>
      )}
    </Stack>
  );
}

function GcsLinksBlock({ detail }: { detail: PromptDetailResponse }) {
  const { gcs } = detail;
  const links: Array<{ label: string; href: string; help?: string }> = [
    {
      label: "HAI 본체 (prefix)",
      href: gcsConsoleUrl(gcs.bucket, gcs.hai_prefix),
      help: "GCS 콘솔에서 그날 BSDA 의 HAI 파일들을 탐색",
    },
    {
      label: "script.py",
      href: gcsConsoleUrl(gcs.bucket, gcs.script_py_path),
    },
    {
      label: "fix_attempts.json",
      href: gcsConsoleUrl(gcs.bucket, gcs.fix_attempts_path),
    },
    {
      label: "validation_report.json",
      href: gcsConsoleUrl(gcs.bucket, gcs.validation_report_path),
    },
  ];
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
        GCS 링크
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        bucket: <code>{gcs.bucket}</code>
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {links.map((l) => (
          <Button
            key={l.label}
            size="small"
            variant="outlined"
            component={Link}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon fontSize="small" />}
            title={l.help}
          >
            {l.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

function FixAttemptsBlock({ detail }: { detail: PromptDetailResponse }) {
  const fa = detail.fix_attempts;
  if (!fa) {
    return (
      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          fix_attempts.json
        </Typography>
        <Typography variant="body2" color="text.secondary">
          이 BSDA 에는 fix_attempts.json 이 아직 없습니다 (fix_loop 미실행 또는 converter PASS).
        </Typography>
      </Box>
    );
  }
  const attempts = fa.attempts ?? [];
  const patterns = fa.fix_patterns_applied ?? [];
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          fix_attempts.json
        </Typography>
        {patterns.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            patterns applied: {patterns.join(", ")}
          </Typography>
        )}
      </Stack>

      {fa.escalation?.reason && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <strong>ESCALATED:</strong> {String(fa.escalation.reason)}
        </Alert>
      )}

      {attempts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          attempts 배열이 비어 있음
        </Typography>
      ) : (
        <Stack spacing={1}>
          {attempts.map((a, i) => (
            <AttemptCard key={i} index={i + 1} attempt={a} />
          ))}
        </Stack>
      )}

      {fa.definition_of_done && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            definition_of_done
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {Object.entries(fa.definition_of_done).map(([k, v]) => (
              <Chip
                key={k}
                size="small"
                color={v ? "success" : "default"}
                variant={v ? "filled" : "outlined"}
                label={`${v ? "✓" : "✗"} ${k}`}
              />
            ))}
          </Stack>
        </Box>
      )}

      {fa.evidence && Object.keys(fa.evidence).length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            evidence
          </Typography>
          <JsonPre value={fa.evidence} />
        </Box>
      )}
    </Box>
  );
}

function AttemptCard({ index, attempt }: { index: number; attempt: FixAttemptEntry }) {
  const status = String(attempt.status ?? "").toUpperCase();
  const passed = status === "PASS" || status === "SUCCESS";
  const failed = status === "FAIL" || status === "FAILED" || status === "ERROR";
  return (
    <Box
      sx={{
        p: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: passed ? "success.50" : failed ? "error.50" : undefined,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>
          #{index}
        </Typography>
        {attempt.fix_pattern && (
          <Chip size="small" variant="outlined" label={attempt.fix_pattern} />
        )}
        {status && (
          <Chip
            size="small"
            color={passed ? "success" : failed ? "error" : "default"}
            label={status}
          />
        )}
        {attempt.started_at && (
          <Typography variant="caption" color="text.secondary">
            {attempt.started_at}
          </Typography>
        )}
      </Stack>
      {attempt.description && (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 0.5 }}>
          {attempt.description}
        </Typography>
      )}
      {attempt.diff_summary && (
        <Typography
          variant="caption"
          sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", display: "block" }}
        >
          {attempt.diff_summary}
        </Typography>
      )}
      {attempt.validation_status && attempt.validation_status !== attempt.status && (
        <Typography variant="caption" color="text.secondary" display="block">
          validation: {attempt.validation_status}
        </Typography>
      )}
    </Box>
  );
}

function ValidationReportBlock({ report }: { report: ValidationReportRaw | null }) {
  if (!report) {
    return (
      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          validation_report.json
        </Typography>
        <Typography variant="body2" color="text.secondary">
          이 BSDA 에는 validation_report.json 이 아직 없습니다 (validate_fix 미실행).
        </Typography>
      </Box>
    );
  }
  const pass = (report.overall_status ?? "").toUpperCase() === "PASS";
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          validation_report.json
        </Typography>
        <Chip
          size="small"
          color={pass ? "success" : "error"}
          label={report.overall_status ?? "(no status)"}
        />
        {report.validation_method && (
          <Typography variant="caption" color="text.secondary">
            method: {report.validation_method}
          </Typography>
        )}
      </Stack>
      {report.overall_reason && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {report.overall_reason}
        </Typography>
      )}
      {report.source_file && (
        <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace" }}>
          source: {report.source_file}
        </Typography>
      )}
      {report.hai_file && (
        <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", mb: 1 }}>
          hai: {report.hai_file}
        </Typography>
      )}
      <Stack spacing={0.5}>
        {(report.checks ?? []).map((c, i) => {
          const ok = String(c.status).toUpperCase() === "PASS";
          return (
            <Box key={i}>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography
                  variant="caption"
                  sx={{ minWidth: 140, fontFamily: "monospace" }}
                >
                  {ok ? "✓" : "✗"} {c.check_name}
                </Typography>
                <Typography
                  variant="caption"
                  color={ok ? "success.main" : "error.main"}
                  fontWeight={600}
                >
                  {c.status}
                </Typography>
                {c.detail && (
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {c.detail}
                  </Typography>
                )}
              </Stack>
              {!ok && c.expected != null && (
                <Stack direction="row" spacing={1} sx={{ pl: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                    기대:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {formatVal(c.expected)}
                  </Typography>
                </Stack>
              )}
              {!ok && c.actual != null && (
                <Stack direction="row" spacing={1} sx={{ pl: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                    실제:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {formatVal(c.actual)}
                  </Typography>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function formatVal(v: unknown): string {
  if (v == null) return "";
  if (Array.isArray(v)) return `[${v.join(", ")}]`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function JsonPre({ value }: { value: unknown }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        bgcolor: "grey.50",
        borderRadius: 1,
        fontSize: 12,
        maxHeight: 240,
        overflow: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontFamily: "monospace",
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  );
}
